// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\rapports-cliniques\index.js

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { 
  Card, Table, Tag, DatePicker, Button, Space, Typography, 
  message, Modal, Form, Input, Divider, Popconfirm,
  Row, Col, Select, InputNumber 
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getClinicReports } from "../../redux/slices/clinicReports/clinicReportsThunk";
import { 
  ExportOutlined, WomanOutlined, SmileOutlined, 
  PhoneOutlined, RocketOutlined, HomeOutlined, 
  DeleteOutlined, EditOutlined, UserOutlined, EnvironmentOutlined, 
  MedicineBoxOutlined, MailOutlined 
} from "@ant-design/icons";
import axiosInstance from "../../services/axiosServicesVisit";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const { Option } = Select;

const ClinicReportsPage = () => {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState({ dateInterval: [] });
  
  // États pour les modales
  const [isClinicModalVisible, setIsClinicModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  
  const [clinicsList, setClinicsList] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [formClinic] = Form.useForm();
  const [formEdit] = Form.useForm();

  const reportsState = useSelector((state) => state.clinicReports);
  const rawData = reportsState?.data?.data || reportsState?.data || []; 
  const loading = reportsState?.loading;

  // --- 1. LOGIQUE DE REGROUPEMENT (FUSION DES JUMEAUX) ---
  const groupedData = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];
    const groups = {};
    
    rawData.forEach(item => {
      // Clé unique : Maman + Téléphone + Date
      const groupKey = `${String(item.momName).trim().toLowerCase()}-${item.momPhone}-${dayjs(item.visitDate).format('YYYYMMDD')}`;
      
      const currentQty = item.rcSamples?.reduce((acc, s) => acc + (s.quantity || 0), 0) || 0;
      const clinicName = item.clinic?.name || item.displayClinicName || "N/A";

      if (!groups[groupKey]) {
        groups[groupKey] = {
          ...item,
          displayClinicName: clinicName,
          babiesList: [{ 
            name: item.babyName, 
            gender: item.babyGender, 
            dob: item.babyDob 
          }],
          totalCoffrets: currentQty
        };
      } else {
        // Ajout du jumeau à la liste du groupe
        groups[groupKey].babiesList.push({ 
          name: item.babyName, 
          gender: item.babyGender, 
          dob: item.babyDob 
        });
        // On additionne la quantité pour le total du groupe
        groups[groupKey].totalCoffrets += currentQty;
        
        // Sécurité nom clinique
        if (groups[groupKey].displayClinicName === "N/A" && item.clinic?.name) {
            groups[groupKey].displayClinicName = item.clinic.name;
        }
      }
    });
    return Object.values(groups);
  }, [rawData]);

  // --- 2. ACTIONS ---
  const fetchReports = useCallback(() => {
    const dateParam = (filters.dateInterval && filters.dateInterval.length === 2) ? filters.dateInterval.join(',') : undefined;
    dispatch(getClinicReports({ pageNumber: 1, limit: 100, filter: { ...filters, dateInterval: dateParam } }));
  }, [filters, dispatch]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const fetchClinicsList = async () => {
    try {
        const res = await axiosInstance.get("/v1/report-clinics/clinics-list");
        setClinicsList(res.data || []);
    } catch (e) { message.error("Erreur cliniques"); }
  };

  // --- 3. MODIFICATION ---
  const handleOpenEdit = (record) => {
    setEditingRecord(record);
    fetchClinicsList();
    formEdit.setFieldsValue({
      ...record,
      visitDate: dayjs(record.visitDate),
      babyDob: record.babyDob ? dayjs(record.babyDob) : null,
      clinicId: record.clinicId || record.clinic?.id,
      coffretQuantity: record.totalCoffrets
    });
    setIsEditModalVisible(true);
  };

  const handleUpdate = async (values) => {
    try {
      const payload = {
        ...values,
        visitDate: values.visitDate.format("YYYY-MM-DD"),
        babyDob: values.babyDob ? values.babyDob.format("YYYY-MM-DD") : null,
      };
      await axiosInstance.patch(`/v1/report-clinics/${editingRecord.id}`, payload);
      message.success("Rapport mis à jour !");
      setIsEditModalVisible(false);
      fetchReports();
    } catch (e) { message.error("Erreur mise à jour"); }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const dateParam = (filters.dateInterval && filters.dateInterval.length === 2) ? filters.dateInterval.join(',') : undefined;
      const response = await axiosInstance.get("/v1/report-clinics/export/csv", {
        params: { dateInterval: dateParam },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export_maternite_${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { message.error("Erreur export"); }
    finally { setExportLoading(false); }
  };

  const columns = [
    {
      title: "Date Visite",
      dataIndex: "visitDate",
      width: 105,
      render: (date) => <Tag color="blue">{dayjs(date).format('DD/MM/YYYY')}</Tag>,
    },
    {
      title: "Clinique",
      width: 150,
      render: (record) => <Text strong style={{ color: '#0e519b' }}>{record.displayClinicName.toUpperCase()}</Text>
    },
    {
      title: "Bénéficiaire(s)",
      key: "beneficiaires",
      width: 220,
      render: (record) => {
        if (record.type === "PROFESSIONAL") {
          return (
            <Space direction="vertical" size={0}>
              <Text strong><MedicineBoxOutlined /> {record.professionalName}</Text>
              <Tag color="green" style={{fontSize:'10px'}}>{record.professionalFunction}</Tag>
            </Space>
          );
        }
        return (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {record.babiesList.map((baby, idx) => (
              <div key={idx} style={{ padding: '4px 0', borderBottom: idx < record.babiesList.length - 1 ? '1px dashed #eee' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <SmileOutlined style={{ fontSize: '14px', color: '#888' }} />
                  <Text strong style={{margin:0}}>{baby.name}</Text>
                  <Tag size="small" color={baby.gender === 'F' ? 'pink' : 'blue'} style={{ fontSize: '10px', margin: 0 }}>
                    {baby.gender}
                  </Tag>
                </div>
                <div style={{ marginLeft: '22px' }}>
                    <Text type="secondary" style={{ fontSize: '10px' }}>
                        Né le : {baby.dob ? dayjs(baby.dob).format('DD/MM/YYYY') : 'N/A'}
                    </Text>
                </div>
              </div>
            ))}
          </Space>
        );
      }
    },
    {
      title: "Maman / Contact",
      width: 180,
      render: (record) => record.type === "BIRTH" ? (
        <Space direction="vertical" size={0}>
          <Text strong><WomanOutlined /> {record.momName}</Text>
          <Text style={{ fontSize: '12px' }}><PhoneOutlined /> {record.momPhone}</Text>
          {record.momEmail && <Text style={{ fontSize: '11px', color: '#1890ff' }}><MailOutlined /> {record.momEmail}</Text>}
          {record.momAddress && <Text type="secondary" style={{ fontSize: '11px' }}><EnvironmentOutlined /> {record.momAddress}</Text>}
        </Space>
      ) : <Text type="secondary">-</Text>,
    },
    {
      title: "Coffret BBW",
      align: 'center',
      width: 100,
      render: (record) => (
        <Tag color="blue" style={{ fontSize: '14px', fontWeight: 'bold' }}>
          x {record.totalCoffrets}
        </Tag>
      )
    },
    {
      title: "Commentaire",
      dataIndex: "comment",
      width: 150,
      render: (text) => <Text italic type="secondary" style={{ fontSize: '11px' }}>{text || "-"}</Text>
    },
    {
        title: "Délégué",
        width: 100,
        render: (record) => <Tag color="cyan" icon={<UserOutlined />} style={{fontSize:10}}>{record.delegate?.firstname}</Tag>
    },
    {
      title: "Action",
      width: 100,
      align: 'center',
      render: (record) => (
        <Space size="middle" style={{ display: 'flex', justifyContent: 'center' }}>
          <Button type="text" size="small" icon={<EditOutlined style={{color: '#1890ff'}} />} onClick={() => handleOpenEdit(record)} />
          <Popconfirm title="Supprimer ?" onConfirm={() => axiosInstance.delete(`/v1/report-clinics/${record.id}`).then(() => fetchReports())}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Card 
        title={<Title level={4} style={{margin:0}}><RocketOutlined /> PLATEFORME CLINIQUE</Title>}
        extra={<Space>
          <Button icon={<HomeOutlined />} onClick={() => { setIsClinicModalVisible(true); fetchClinicsList(); }}>Gérer les cliniques</Button>
          <Button type="primary" icon={<ExportOutlined />} onClick={handleExportCSV} loading={exportLoading}>Exporter CSV</Button>
        </Space>}
      >
        <div style={{ marginBottom: '20px' }}>
          <Space>
            <RangePicker onChange={(dates) => setFilters({ ...filters, dateInterval: dates ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')] : [] })} />
            <Button onClick={() => setFilters({ dateInterval: [] })}>Réinitialiser</Button>
          </Space>
        </div>
        <Table columns={columns} dataSource={groupedData} loading={loading} rowKey="id" bordered pagination={false} scroll={{ x: 1200 }} />
      </Card>

      {/* MODALE DE MODIFICATION */}
      <Modal title="Modifier le rapport" open={isEditModalVisible} onCancel={() => setIsEditModalVisible(false)} onOk={() => formEdit.submit()} width={600}>
        <Form form={formEdit} layout="vertical" onFinish={handleUpdate}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="visitDate" label="Date Visite"><DatePicker style={{width:'100%'}} format="DD/MM/YYYY" /></Form.Item></Col>
            <Col span={12}><Form.Item name="clinicId" label="Clinique"><Select>{clinicsList.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}</Select></Form.Item></Col>
          </Row>
          <Divider style={{margin: '12px 0'}} />
          {editingRecord?.type === 'BIRTH' ? (
            <>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="babyName" label="Nom Bébé"><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="babyDob" label="Né le"><DatePicker style={{width:'100%'}} format="DD/MM/YYYY" /></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="momName" label="Nom Maman"><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="momPhone" label="Téléphone"><Input /></Form.Item></Col>
              </Row>
              <Form.Item name="momEmail" label="Email"><Input /></Form.Item>
              <Form.Item name="momAddress" label="Adresse"><Input /></Form.Item>
            </>
          ) : (
            <Form.Item name="professionalName" label="Nom Professionnel"><Input /></Form.Item>
          )}
          <Form.Item name="coffretQuantity" label="Nombre de coffrets"><InputNumber min={0} style={{width:'100%'}} /></Form.Item>
          <Form.Item name="comment" label="Commentaire"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>

      {/* MODALE GESTION CLINIQUE */}
      <Modal title="Gestion Cliniques" open={isClinicModalVisible} onCancel={() => setIsClinicModalVisible(false)} footer={null} width={600}>
        <Form layout="inline" onFinish={(v) => axiosInstance.post("/v1/report-clinics/clinics", v).then(() => { message.success("Ajoutée"); fetchClinicsList(); })}>
          <Form.Item name="name" rules={[{required: true}]}><Input placeholder="Nom" style={{width:180}}/></Form.Item>
          <Button type="primary" htmlType="submit">Ajouter</Button>
        </Form>
        <Divider />
        <Table dataSource={clinicsList} rowKey="id" size="small" columns={[{ title: 'Nom', dataIndex: 'name' }, { title: 'Action', render: (r) => <Button type="text" danger icon={<DeleteOutlined />} onClick={() => axiosInstance.delete(`/v1/report-clinics/clinics/${r.id}`).then(() => fetchClinicsList())} /> }]} />
      </Modal>
    </div>
  );
};

export default ClinicReportsPage;